/*
 * SessionBuilder — "Start a new session", the heart of the Practice page.
 * Three steps in one panel: what to practise (a role, or custom topics), which
 * practice types, and how long. Create session hands the configuration to
 * SessionRunner, which interleaves every chosen type in one continuous run.
 */
import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/prepare-up-session.css';
import APIService from '../../services/APIService';
import { loadGoal } from './HomeDashboard';
import { ROLES, focusOptions, roleForGoal, roleOf } from './interviewData';
import { ACTIVITIES, LENGTHS, allocate, actOf, buildSteps, estMinutes, recommendedTypes, totalItems } from './sessionPlan';
import { IconArrowRight, IconBolt, IconBook, IconCards, IconCheck, IconChevDown, IconClock, IconList, IconSpark, IconTarget } from './icons';

const ICONS = { mcq: IconList, flashcards: IconCards, theory: IconBook, case: IconTarget, rapid: IconBolt };
const DEFAULT_TYPES = ['mcq', 'flashcards', 'theory', 'case'];

/* Soft ridgelines and a summit flag — the "closer to your goal" atmosphere. */
const HeroArt = () => (
  <svg className="ses-hero-art" viewBox="0 0 640 220" preserveAspectRatio="xMaxYMax slice" aria-hidden="true" focusable="false">
    <path d="M0 220 L150 120 L230 165 L360 70 L470 140 L560 95 L640 130 L640 220 Z" className="r1" />
    <path d="M120 220 L300 110 L400 160 L520 52 L640 118 L640 220 Z" className="r2" />
    <path d="M260 220 C360 196 420 204 500 184 S620 170 640 176 L640 220 Z" className="r3" />
    <path d="M520 52 L520 18" className="pole" />
    <path d="M520 18 L544 25 L520 33 Z" className="flag" />
  </svg>
);

export default function SessionBuilder({ hub, seed, resumable, onResume, onDiscard, onCreate }) {
  const goalRole = roleForGoal(loadGoal());
  const [source, setSource] = useState(seed?.topics?.length ? 'topics' : 'role');   // role | topics
  const [roleId, setRoleId] = useState(goalRole?.id || ROLES.find((r) => r.rootIds.length).id);
  const [topics, setTopics] = useState(seed?.topics || []);                           // [{ id, name }]
  const [types, setTypes] = useState(seed?.types?.length ? seed.types : DEFAULT_TYPES);
  const [recommended, setRecommended] = useState(false);
  const [minutes, setMinutes] = useState(seed?.minutes || 30);
  const [due, setDue] = useState([]);
  const [tried, setTried] = useState(false);
  useEffect(() => { APIService.getEngineMemory().then((d) => setDue(d?.due_reviews || [])).catch(() => setDue([])); }, []);

  const role = roleOf(roleId);
  const scope = source === 'role' ? (role?.rootIds || []) : topics.map((t) => t.id);
  const scopeLabel = source === 'role' ? role?.title : topics.map((t) => t.name).join(', ');
  const dueInScope = due.filter((d) => scope.some((id) => d.concept_id === id || String(d.concept_id).startsWith(`${id}.`))).length;
  const roleSkills = useMemo(() => focusOptions(role, hub), [role, hub]);

  // Recommended mix: recall + check + explain always, a case study when there's
  // time, rapid fire when reviews are due in this scope. You can still edit it.
  const chosen = recommended ? recommendedTypes({ minutes, dueCount: dueInScope }) : types;
  const counts = useMemo(() => allocate(chosen, minutes), [chosen.join(','), minutes]);   // eslint-disable-line react-hooks/exhaustive-deps
  const toggleType = (id) => {
    setTypes(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]);
    setRecommended(false);
  };
  const toggleTopic = (o) => setTopics((p) => (p.some((x) => x.id === o.id) ? p.filter((x) => x.id !== o.id) : [...p, o]));

  // custom topics: the hub's skills, grouped by competency
  const groups = useMemo(() => (hub || []).map((r) => ({ id: r.id, name: r.name, skills: (r.children || []).slice(0, r.id === 'dsa' ? 16 : 40).map((c) => ({ id: c.id, name: c.name })) })), [hub]);
  const problem = !scope.length ? (source === 'role' ? 'This role has no practice content yet — pick another.' : 'Pick at least one topic.')
    : !chosen.length ? 'Pick at least one practice type.' : '';

  const create = () => {
    setTried(true);
    if (problem) return;
    const steps = buildSteps(counts);
    if (!steps.length) return;
    onCreate({ scopeIds: scope, scopeLabel, source, roleId: source === 'role' ? roleId : null, types: chosen, minutes, counts, order: 'mixed', steps });
  };

  return (
    <section className="ses-builder" aria-labelledby="ses-title">
      <div className="ses-hero">
        <HeroArt />
        <span className="ses-hero-note" aria-hidden="true"><b>Practice today.</b><span>Get closer to your goal.</span></span>
        <span className="pu-kicker">Practice</span>
        <h1 id="ses-title">Start a new session</h1>
        <p>Choose what you want to practise and build a session around your goals — one continuous run that mixes the question types you pick.</p>
      </div>

      {resumable && (
        <div className="ses-resume">
          <div><b>Resume your session</b><span>{resumable.cfg.scopeLabel} · {resumable.idx} of {resumable.cfg.steps.length} activities done</span></div>
          <button type="button" className="pu-link" onClick={onDiscard}>Discard</button>
          <button type="button" className="pu-btn pu-btn-sm pu-btn-primary" onClick={onResume}>Resume <IconArrowRight size={13} /></button>
        </div>
      )}

      <div className="ses-card">
        {/* 1 */}
        <section className="ses-step" aria-labelledby="ses-1">
          <span className="ses-n" aria-hidden="true">1</span>
          <div className="ses-step-body">
            <div className="ses-step-head">
              <div><h2 id="ses-1">Choose what to practice</h2><p>Select a role or topics. We’ll tailor the session to your goal.</p></div>
              <div className="ses-toggle" role="radiogroup" aria-label="Practise by">
                {[['role', 'Role'], ['topics', 'Custom topics']].map(([id, l]) => (
                  <button key={id} type="button" role="radio" aria-checked={source === id} className={source === id ? 'on' : ''} onClick={() => setSource(id)}>{l}</button>
                ))}
              </div>
            </div>
            <div className="ses-what">
              {source === 'role' ? (
                <div className="ses-field">
                  <label htmlFor="ses-role">Target role</label>
                  <span className="kh-select ses-select">
                    <select id="ses-role" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                      {ROLES.map((r) => <option key={r.id} value={r.id} disabled={!r.rootIds.length}>{r.title}{r.rootIds.length ? '' : ' — coming soon'}{goalRole?.id === r.id ? ' · your goal' : ''}</option>)}
                    </select>
                    <IconChevDown size={14} />
                  </span>
                  {roleSkills.length > 0 && (
                    <small className="ses-role-skills">
                      {roleSkills.slice(0, 5).map((s) => s.name).join(' · ')}{roleSkills.length > 5 ? ` · +${roleSkills.length - 5} more` : ''}
                    </small>
                  )}
                </div>
              ) : (
                <div className="ses-topics" role="group" aria-label="Topics">
                  {topics.length > 0 && <p className="ses-picked">{topics.length} selected <button type="button" className="pu-link" onClick={() => setTopics([])}>Clear</button></p>}
                  {groups.map((g) => (
                    <div key={g.id}>
                      <span className="ses-topic-group">{g.name}</span>
                      <div className="ses-chips">
                        {g.skills.map((o) => {
                          const on = topics.some((t) => t.id === o.id);
                          return <button key={o.id} type="button" className={on ? 'on' : ''} aria-pressed={on} onClick={() => toggleTopic(o)}>{on && <IconCheck size={11} />}{o.name}</button>;
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <aside className="ses-personal">
                <span aria-hidden="true"><IconTarget size={20} /></span>
                <div><b>Personalised for your goal</b><p>Questions within your selection are picked from your learning history and skill gaps.</p></div>
              </aside>
            </div>
          </div>
        </section>

        {/* 2 */}
        <section className="ses-step" aria-labelledby="ses-2">
          <span className="ses-n" aria-hidden="true">2</span>
          <div className="ses-step-body">
            <div className="ses-step-head">
              <div><h2 id="ses-2">Choose practice types</h2><p>Pick one for focused practice, or several for a guided mixed session.</p></div>
              <div className="ses-reco">
                <button type="button" role="switch" aria-checked={recommended} aria-labelledby="ses-reco-l" className={`ses-switch${recommended ? ' on' : ''}`} onClick={() => setRecommended((v) => !v)}><i /></button>
                <span id="ses-reco-l"><b><IconSpark size={13} /> Recommended mix</b>
                  <small>{dueInScope ? `Recall, check and explain — plus rapid fire for ${dueInScope} due review${dueInScope === 1 ? '' : 's'}.` : 'Recall, check and explain, with a case study when there’s time.'}</small></span>
              </div>
            </div>
            <div className="ses-types" role="group" aria-label="Practice types">
              {ACTIVITIES.map((a) => {
                const Icon = ICONS[a.id];
                const on = chosen.includes(a.id);
                return (
                  <button key={a.id} type="button" className={`ses-type t-${a.id}${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggleType(a.id)}>
                    <span className="ses-type-icon" aria-hidden="true"><Icon size={18} /></span>
                    <span className="ses-check" aria-hidden="true">{on && <IconCheck size={12} />}</span>
                    <b>{a.label}</b><small>{a.blurb}</small>
                    <em>{on && counts[a.id] ? `${counts[a.id]} in this session` : ' '}</em>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* 3 */}
        <section className="ses-step" aria-labelledby="ses-3">
          <span className="ses-n" aria-hidden="true">3</span>
          <div className="ses-step-body">
            <div className="ses-step-head"><div><h2 id="ses-3">Set session length <span className="ses-optional">(optional)</span></h2><p>Counts adapt to the types you picked — a case study takes far longer than a flashcard.</p></div></div>
            <div className="ses-foot">
              <div className="ses-lengths" role="radiogroup" aria-label="Session length">
                {LENGTHS.map((l) => {
                  const c = allocate(chosen, l.min);
                  return (
                    <button key={l.min} type="button" role="radio" aria-checked={minutes === l.min} className={minutes === l.min ? 'on' : ''} onClick={() => setMinutes(l.min)}>
                      <IconClock size={16} /><span><b>{l.label}</b><small>{chosen.length ? `${totalItems(c)} activities` : '—'}</small></span>
                    </button>
                  );
                })}
              </div>
              <button type="button" className="pu-btn pu-btn-primary ses-create" aria-disabled={!!problem} onClick={create}>Create session <IconArrowRight size={15} /></button>
            </div>
            {problem
              ? ((tried || !chosen.length) && <p className="ses-summary warn" role="alert">{problem}</p>)
              : (
                <p className="ses-summary">
                  <b>{scopeLabel}</b> · {Object.entries(counts).map(([k, n]) => `${n} ${n === 1 ? actOf(k).one : actOf(k).label}`).join(' · ')} · about {estMinutes(counts)} min (estimate) · {chosen.length > 1 ? 'a guided conversation that mixes them throughout' : `opens focused ${actOf(chosen[0]).label} practice`}
                </p>
              )}
          </div>
        </section>
      </div>
    </section>
  );
}
